import { DownOutlined, FileTextOutlined, UpOutlined } from '@ant-design/icons'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography, useTheme } from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams, GridRowsProp, GridSortModel } from '@mui/x-data-grid'
import Paper from 'components/@extended/Paper'
import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { PrepareLedgerItem } from 'types/accruals'
import { formatDisplayDate } from 'types/periods'

interface DebitCreditReviewTableProps {
  lineItems: PrepareLedgerItem[]
  adjustments: Record<string, AdjustmentValue>
  checkedItems: Record<string, boolean>
  notes: Record<string, string>
  periodIdToLabel: Record<string, string>
  isLoading?: boolean
}

interface AdjustmentValue {
  percentChange: number
  fixedChange: number
  activeType: 'percentage' | 'currency'
  currentBalance: number
  originalBalance: number
}

interface DataGridRow {
  id: string
  type: 'user' | 'item'
  userId?: string
  firstName?: string
  lastName?: string
  lineItemName?: string
  debit: number
  credit: number
  notes?: string
  parentId?: string
  level: number
  originalItem?: PrepareLedgerItem
  createdAt?: string
}

const DebitCreditReviewTable: FC<DebitCreditReviewTableProps> = ({
  lineItems,
  adjustments,
  checkedItems,
  notes,
  periodIdToLabel,
  isLoading = false,
}) => {
  const theme = useTheme()
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'lastName', sort: 'asc' }])
  const [notesModal, setNotesModal] = useState<{
    open: boolean
    item: DataGridRow | null
  }>({ open: false, item: null })
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const getCompositeId = useCallback(
    (item: PrepareLedgerItem) => item.id || `${item.allocationTableId}-${item.allocationRowId}`,
    []
  )

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const getItemDebit = useCallback((item: PrepareLedgerItem) => {
    // Calculate the change from previous balance to current balance
    const balanceChange = item.balance - item.previousBalance
    return balanceChange < 0 ? Math.abs(balanceChange) : 0
  }, [])

  const getItemCredit = useCallback((item: PrepareLedgerItem) => {
    // Calculate the change from previous balance to current balance
    const balanceChange = item.balance - item.previousBalance
    return balanceChange > 0 ? balanceChange : 0
  }, [])

  // Prepare data for DataGrid with custom sorting
  const rows: GridRowsProp<DataGridRow> = useMemo(() => {
    const checkedItemsFiltered = lineItems.filter((item) => checkedItems[getCompositeId(item)] ?? true)

    // Debug logging
    console.log('Ledger Data Debug:', {
      totalItems: checkedItemsFiltered.length,
      firstFewItems: checkedItemsFiltered.slice(0, 3).map((item) => ({
        userId: item.userId,
        firstName: item.firstName,
        lastName: item.lastName,
        balance: item.balance,
        allocationRowName: item.allocationRowName,
      })),
    })

    const userGroups: Record<string, PrepareLedgerItem[]> = {}
    checkedItemsFiltered.forEach((item) => {
      const userKey = `${item.userId}-${item.firstName}-${item.lastName}`
      if (!userGroups[userKey]) {
        userGroups[userKey] = []
      }
      userGroups[userKey].push(item)
    })

    console.log(
      'User Groups:',
      Object.keys(userGroups).map((key) => ({
        userKey: key,
        itemCount: userGroups[key].length,
        balances: userGroups[key].map((item) => item.balance),
      }))
    )

    // Create user data with totals for sorting
    const userEntries = Object.entries(userGroups).map(([userKey, userItems]) => {
      const user = userItems[0]
      const userDebitTotal = userItems.reduce((sum, item) => {
        const balanceChange = item.balance - item.previousBalance
        const debit = balanceChange < 0 ? Math.abs(balanceChange) : 0
        return sum + debit
      }, 0)
      const userCreditTotal = userItems.reduce((sum, item) => {
        const balanceChange = item.balance - item.previousBalance
        const credit = balanceChange > 0 ? balanceChange : 0
        return sum + credit
      }, 0)

      return {
        userKey,
        userItems,
        user,
        userDebitTotal,
        userCreditTotal,
      }
    })

    // Apply sorting to users only
    if (sortModel.length > 0) {
      const sortField = sortModel[0].field
      const sortDirection = sortModel[0].sort

      userEntries.sort((a, b) => {
        let aValue: any, bValue: any

        switch (sortField) {
          case 'lastName':
            aValue = a.user.lastName
            bValue = b.user.lastName
            break
          case 'firstName':
            aValue = a.user.firstName
            bValue = b.user.firstName
            break
          case 'debit':
            aValue = a.userDebitTotal
            bValue = b.userDebitTotal
            break
          case 'credit':
            aValue = a.userCreditTotal
            bValue = b.userCreditTotal
            break
          case 'lineItemName':
            aValue = `${a.user.firstName} ${a.user.lastName}`
            bValue = `${b.user.firstName} ${b.user.lastName}`
            break
          case 'notes':
            aValue = a.user.lastName // Default to lastName for notes column sorting
            bValue = b.user.lastName
            break
          default:
            aValue = a.user.lastName
            bValue = b.user.lastName
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const result = aValue.localeCompare(bValue)
          return sortDirection === 'asc' ? result : -result
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          const result = aValue - bValue
          return sortDirection === 'asc' ? result : -result
        }

        return 0
      })
    }

    // Build final rows maintaining user groupings
    const result: DataGridRow[] = []

    userEntries.forEach(({ userKey, userItems, user, userDebitTotal, userCreditTotal }, userIndex) => {
      // Debug user totals
      console.log(`User ${user.firstName} ${user.lastName}:`, {
        userKey,
        userDebitTotal,
        userCreditTotal,
        itemBalances: userItems.map((item) => ({
          balance: item.balance,
          rowName: item.allocationRowName,
        })),
      })

      // Add user header row with stable ID
      result.push({
        id: userKey,
        type: 'user',
        userId: user.userId,
        firstName: user.firstName,
        lastName: user.lastName,
        debit: userDebitTotal,
        credit: userCreditTotal,
        level: 0,
      })

      // Add line items if expanded
      if (isExpanded) {
        // Sort user items by created_at in ascending order (oldest first)
        const sortedUserItems = [...userItems].sort((a, b) => {
          const parseDate = (dateStr: string) => {
            try {
              // Handle the format "08-12-2025 12:19:27"
              const [datePart, timePart] = dateStr.split(' ')
              const [month, day, year] = datePart.split('-')
              const [hour, minute, second] = timePart.split(':')

              return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second || '00'}`).getTime()
            } catch (error) {
              // Fallback for ISO format or other formats
              return new Date(dateStr).getTime()
            }
          }

          const dateA = parseDate(a.createdAt)
          const dateB = parseDate(b.createdAt)
          return dateA - dateB
        })

        sortedUserItems.forEach((item) => {
          const compositeId = getCompositeId(item)
          const itemNotes = notes[compositeId]
          const lineItemName = `${periodIdToLabel[item.periodId] || 'Unknown Period'}${item.allocationTableName ? ` | ${item.allocationTableName}` : ''} | ${item.allocationRowName}`

          // Calculate debit/credit directly from this specific item's balance
          const balanceChange = item.balance - item.previousBalance
          const itemDebit = balanceChange < 0 ? Math.abs(balanceChange) : 0
          const itemCredit = balanceChange > 0 ? balanceChange : 0

          console.log(`  Line item ${item.allocationRowName}:`, {
            balance: item.balance,
            previousBalance: item.previousBalance,
            balanceChange,
            itemDebit,
            itemCredit,
            compositeId,
          })

          result.push({
            id: compositeId,
            type: 'item',
            parentId: userKey,
            lineItemName,
            debit: itemDebit,
            credit: itemCredit,
            notes: itemNotes,
            level: 1,
            originalItem: item,
            createdAt: item.createdAt,
          })
        })
      }
    })

    return result
  }, [
    lineItems,
    checkedItems,
    getCompositeId,
    adjustments,
    notes,
    periodIdToLabel,
    getItemDebit,
    getItemCredit,
    sortModel,
    isExpanded,
  ])

  const columns: GridColDef<DataGridRow>[] = [
    {
      field: 'lineItemName',
      headerName: 'User / Line Item',
      flex: 1,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams<DataGridRow>) => {
        if (params.row.type === 'user') {
          return (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                pl: params.row.level * 2,
                fontSize: '0.875rem',
              }}
            >
              {params.row.firstName} {params.row.lastName}
            </Typography>
          )
        }
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              pl: params.row.level * 4,
            }}
          >
            {params.row.lineItemName}
          </Typography>
        )
      },
    },
    {
      field: 'debit',
      headerName: 'Debit',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<DataGridRow>) => {
        const debit = params.row.debit
        if (debit > 0) {
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: params.row.type === 'user' ? 'bold' : 'normal',
                color: 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              ${debit.toFixed(2)}
            </Typography>
          )
        }
        return (
          <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
            -
          </Typography>
        )
      },
    },
    {
      field: 'credit',
      headerName: 'Credit',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<DataGridRow>) => {
        const credit = params.row.credit
        if (credit > 0) {
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: params.row.type === 'user' ? 'bold' : 'normal',
                color: 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              ${credit.toFixed(2)}
            </Typography>
          )
        }
        return (
          <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
            -
          </Typography>
        )
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<DataGridRow>) => {
        if (params.row.type === 'user' || !params.row.createdAt) {
          return null
        }

        const formatDate = (dateStr: string) => {
          try {
            // Try to parse the date string and format using our standard utility
            return formatDisplayDate(dateStr)
          } catch (error) {
            // Fallback - try to extract just the date part if formatting fails
            return dateStr.split(' ')[0] || dateStr
          }
        }

        return (
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {formatDate(params.row.createdAt)}
          </Typography>
        )
      },
    },
    {
      field: 'notes',
      headerName: 'Info',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography>Info</Typography>
          <IconButton size="small" onClick={toggleExpanded}>
            {isExpanded ? <UpOutlined /> : <DownOutlined />}
          </IconButton>
        </Box>
      ),
      renderCell: (params: GridRenderCellParams<DataGridRow>) => {
        if (params.row.type === 'user') {
          return null
        }

        const hasNotes = params.row.notes && params.row.notes.trim() !== ''
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IconButton
              size="small"
              onClick={() => setNotesModal({ open: true, item: params.row })}
              sx={{
                color: hasNotes ? theme.palette.primary.main : theme.palette.text.disabled,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <FileTextOutlined style={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
        )
      },
    },
  ]

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Typography>Loading...</Typography>
      </Box>
    )
  }

  return (
    <>
      <Paper sx={{ height: '100%', width: '100%', minHeight: 300, display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          disableColumnSelector
          disableColumnMenu
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          sortingMode="server"
          density="standard"
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
          pageSizeOptions={[50, 100, 200]}
          getRowClassName={(params) => {
            const row = params.row as DataGridRow
            if (row.type === 'user') {
              // Use a stable index based on userId to ensure consistent coloring
              const userRows = rows.filter((r) => r.type === 'user')
              const userIndex = userRows.findIndex((r) => r.id === row.id)
              return userIndex % 2 === 0 ? 'user-row-even' : 'user-row-odd'
            } else {
              // Find parent user row and use its index for consistent coloring
              const parentRow = rows.find((r) => r.id === row.parentId)
              if (parentRow) {
                const userRows = rows.filter((r) => r.type === 'user')
                const userIndex = userRows.findIndex((r) => r.id === parentRow.id)
                return userIndex % 2 === 0 ? 'item-row-even' : 'item-row-odd'
              }
              return 'item-row-even'
            }
          }}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              borderTop: `1px solid ${theme.palette.divider}`,
              borderBottom: `2px solid ${theme.palette.divider} !important`,
              backgroundColor: theme.palette.background.default,
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.875rem',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              userSelect: 'none',
              '&:focus': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: `${theme.palette.action.hover} !important`,
              },
              '&.user-row-even, &.item-row-even': {
                backgroundColor: theme.palette.background.paper,
              },
              '&.user-row-odd, &.item-row-odd': {
                backgroundColor: theme.palette.action.hover,
              },
            },
            '& .MuiDataGrid-cell': {
              paddingTop: '12px',
              paddingBottom: '12px',
              fontSize: '0.875rem',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
            },
          }}
        />
      </Paper>

      {/* Notes Modal */}
      <Dialog open={notesModal.open} onClose={() => setNotesModal({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Line Item Details</DialogTitle>
        <DialogContent>
          {notesModal.item && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Line Item:
                </Typography>
                <Typography variant="body1">{notesModal.item.lineItemName}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Amount:
                </Typography>
                <Typography variant="body1">
                  {notesModal.item.debit > 0
                    ? `$${notesModal.item.debit.toFixed(2)} (Debit)`
                    : `$${notesModal.item.credit.toFixed(2)} (Credit)`}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Notes:
                </Typography>
                <Typography variant="body1" sx={{ fontStyle: notesModal.item.notes ? 'normal' : 'italic' }}>
                  {notesModal.item.notes || 'No notes available'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default DebitCreditReviewTable
